'use client';
import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["activeKey", "onSelect"],
  _excluded2 = ["as", "title", "onClose", "onOpen", "onToggle", "trigger", "placement", "toggleAs", "toggleClassName", "open", "defaultOpen", "classPrefix", "className", "disabled", "children", "menuStyle", "style"],
  _excluded3 = ["open"],
  _excluded4 = ["open"];
import React, { useContext, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import DropdownMenu from "./DropdownMenu.js";
import { PLACEMENT_8 } from "../internals/constants/index.js";
import { useClassNames } from "../internals/hooks/index.js";
import { mergeRefs, placementPolyfill, warnOnce } from "../internals/utils/index.js";
import { deprecatePropType, oneOf } from "../internals/propTypes/index.js";
import DropdownItem from "./DropdownItem.js";
import DropdownContext from "./DropdownContext.js";
import Menu from "../internals/Menu/Menu.js";
import DropdownToggle from "./DropdownToggle.js";
import kebabCase from 'lodash/kebabCase';
import NavContext from "../Nav/NavContext.js";
import { initialState, reducer } from "./DropdownState.js";
import Nav from "../Nav/index.js";
import DropdownSeparator from "./DropdownSeparator.js";
/**
 * The `Dropdown` component is used to select an option from a set of options.
 * @see https://rsuitejs.com/components/dropdown
 *
 * The `<Dropdown>` API
 * - When used inside `<Sidenav>`, renders a `<TreeviewRootItem>`;
 * - Otherwise renders a `<MenuRoot>`
 */
var Dropdown = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var activeKey = props.activeKey,
    onSelect = props.onSelect,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  var _rest$as = rest.as,
    Component = _rest$as === void 0 ? 'div' : _rest$as,
    title = rest.title,
    onClose = rest.onClose,
    onOpen = rest.onOpen,
    onToggle = rest.onToggle,
    _rest$trigger = rest.trigger,
    trigger = _rest$trigger === void 0 ? 'click' : _rest$trigger,
    _rest$placement = rest.placement,
    placement = _rest$placement === void 0 ? 'bottomStart' : _rest$placement,
    toggleAs = rest.toggleAs,
    toggleClassName = rest.toggleClassName,
    open = rest.open,
    defaultOpen = rest.defaultOpen,
    _rest$classPrefix = rest.classPrefix,
    classPrefix = _rest$classPrefix === void 0 ? 'dropdown' : _rest$classPrefix,
    className = rest.className,
    disabled = rest.disabled,
    children = rest.children,
    menuStyle = rest.menuStyle,
    style = rest.style,
    toggleProps = _objectWithoutPropertiesLoose(rest, _excluded2);
  var nav = useContext(NavContext);
  var _useClassNames = useClassNames(classPrefix),
    merge = _useClassNames.merge,
    withClassPrefix = _useClassNames.withClassPrefix;
  var _useClassNames2 = useClassNames('dropdown-menu'),
    withMenuClassPrefix = _useClassNames2.withClassPrefix,
    mergeMenuClassName = _useClassNames2.merge;
  var menuButtonTriggers = useMemo(function () {
    if (!trigger) {
      return undefined;
    }
    var triggerMap = {
      hover: 'mouseover',
      click: 'click',
      contextMenu: 'contextmenu'
    };
    if (!Array.isArray(trigger)) {
      return [triggerMap[trigger]];
    }
    return trigger.map(function (t) {
      return triggerMap[t];
    });
  }, [trigger]);
  var _useReducer = useReducer(reducer, initialState),
    items = _useReducer[0].items,
    dispatch = _useReducer[1];
  var hasSelectedItem = useMemo(function () {
    return items.some(function (item) {
      return item.props.selected;
    });
  }, [items]);
  var dropdownContextValue = useMemo(function () {
    return {
      activeKey: activeKey,
      onSelect: onSelect,
      hasSelectedItem: hasSelectedItem,
      dispatch: dispatch
    };
  }, [activeKey, onSelect, hasSelectedItem, dispatch]);

  // Deprecate <Dropdown> within <Nav> usage
  // in favor of <Nav.Menu> API
  if (nav) {
    warnOnce('Usage of <Dropdown> within <Nav> is deprecated. Replace with <Nav.Menu>');
    return /*#__PURE__*/React.createElement(Nav.Menu, _extends({
      ref: ref
    }, props));
  }
  var renderMenuButton = function renderMenuButton(menuButtonProps, menuButtonRef) {
    return /*#__PURE__*/React.createElement(DropdownToggle, _extends({
      ref: menuButtonRef,
      as: toggleAs,
      className: toggleClassName,
      placement: placement,
      disabled: disabled
    }, omit(menuButtonProps, ['open']), omit(toggleProps, ['data-testid'])), title);
  };
  return /*#__PURE__*/React.createElement(DropdownContext.Provider, {
    value: dropdownContextValue
  }, /*#__PURE__*/React.createElement(Menu, {
    open: open,
    defaultOpen: defaultOpen,
    menuButtonText: title,
    renderMenuButton: renderMenuButton,
    disabled: disabled,
    openMenuOn: menuButtonTriggers,
    renderMenuPopup: function renderMenuPopup(_ref, popupRef) {
      var open = _ref.open,
        popupProps = _objectWithoutPropertiesLoose(_ref, _excluded3);
      var menuClassName = mergeMenuClassName(className, withMenuClassPrefix({}));
      return /*#__PURE__*/React.createElement("ul", _extends({
        ref: popupRef,
        className: menuClassName,
        style: menuStyle,
        hidden: !open
      }, popupProps), children);
    },
    onToggleMenu: function onToggleMenu(open) {
      onToggle === null || onToggle === void 0 ? void 0 : onToggle(open);
      if (open) {
        onOpen === null || onOpen === void 0 ? void 0 : onOpen();
      } else {
        onClose === null || onClose === void 0 ? void 0 : onClose();
      }
    }
  }, function (_ref2, menuContainerRef) {
    var _withClassPrefix;
    var open = _ref2.open,
      menuContainer = _objectWithoutPropertiesLoose(_ref2, _excluded4);
    var classes = merge(className, withClassPrefix((_withClassPrefix = {}, _withClassPrefix["placement-" + kebabCase(placementPolyfill(placement))] = !!placement, _withClassPrefix.disabled = disabled, _withClassPrefix.open = open, _withClassPrefix['selected-within'] = hasSelectedItem, _withClassPrefix)));
    return /*#__PURE__*/React.createElement(Component, _extends({
      ref: mergeRefs(ref, menuContainerRef),
      className: classes
    }, menuContainer, pick(toggleProps, ['data-testid']), {
      style: style
    }));
  }));
});
Dropdown.Item = DropdownItem;
Dropdown.Menu = DropdownMenu;
Dropdown.Separator = DropdownSeparator;
Dropdown.displayName = 'Dropdown';
Dropdown.propTypes = {
  activeKey: PropTypes.any,
  classPrefix: PropTypes.string,
  trigger: PropTypes.oneOfType([PropTypes.array, oneOf(['click', 'hover', 'contextMenu'])]),
  placement: oneOf(PLACEMENT_8),
  title: PropTypes.node,
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  menuStyle: PropTypes.object,
  className: PropTypes.string,
  toggleClassName: PropTypes.string,
  children: PropTypes.node,
  open: deprecatePropType(PropTypes.bool),
  eventKey: PropTypes.any,
  as: PropTypes.elementType,
  toggleAs: PropTypes.elementType,
  noCaret: PropTypes.bool,
  style: PropTypes.object,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  onToggle: PropTypes.func,
  onSelect: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onContextMenu: PropTypes.func,
  onClick: PropTypes.func,
  renderToggle: PropTypes.func
};
export default Dropdown;